import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { UserContext } from '@/components/App';
import { initialsFor } from '@/components/CorporateUsers/shippers/utils';
import {
    AccountBanner,
    BannerActions,
    BannerAvatar,
    BannerButton,
    BannerEyebrow,
    BannerLeft,
    BannerMeta,
    BannerName,
    BannerRight,
    PageSurface,
    TabButton,
    TabStrip,
} from './blocks';
import { TABS } from './constants';
import { slugForTabIndex, tabIndexForSlug } from './utils';
import { useShipperScope } from './hooks';
import ShipperGate from './ShipperGate';
import Overview from './tabs/Overview';
import CompanyInfo from './tabs/CompanyInfo';
import Integrations from './tabs/Integrations';
import TariffsTags from './tabs/TariffsTags';
import KeyDocs from './tabs/KeyDocs';
import Operational from './tabs/Operational';
import Communications from './tabs/Communications';

const TAB_COMPONENTS = {
    overview: Overview,
    'company-info': CompanyInfo,
    integrations: Integrations,
    'tariffs-tags': TariffsTags,
    'key-docs': KeyDocs,
    operational: Operational,
    communications: Communications,
};

const CorporateShipper = () => {
    const { client_id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    // Everything outside the provider at the bottom reads the admin's context.
    const { isPermitted, shipper, loading, error, shipperContext } = useShipperScope(client_id);
    const tabIndex = tabIndexForSlug(searchParams.get('tab'));
    const ActiveTab = TAB_COMPONENTS[TABS[tabIndex].slug];

    if (!isPermitted || loading || !shipper) {
        return <ShipperGate isPermitted={isPermitted} loading={loading} error={error} shipper={shipper} />;
    }

    return (
        <PageSurface>
            <AccountBanner>
                <BannerLeft>
                    <BannerAvatar>{initialsFor(shipper.business_name)}</BannerAvatar>
                    <div>
                        <BannerEyebrow>Shipper</BannerEyebrow>
                        <BannerName>{shipper.business_name || 'Unnamed shipper'}</BannerName>
                        <BannerMeta>Client ID: {shipper.client_id}</BannerMeta>
                    </div>
                </BannerLeft>
                <BannerRight>
                    {/* OWNER goes here once the record's creator is available. */}
                    <BannerActions>
                        {/* Send reminder removed — see REMOVED_ACTIONS in ./constants.js. */}
                        <BannerButton onClick={() => setSearchParams({ tab: 'company-info' })}>Edit</BannerButton>
                    </BannerActions>
                </BannerRight>
            </AccountBanner>

            <TabStrip role="tablist">
                {TABS.map((tab, index) => (
                    <TabButton
                        key={tab.slug}
                        type="button"
                        role="tab"
                        aria-selected={index === tabIndex}
                        selected={index === tabIndex}
                        onClick={() => setSearchParams({ tab: slugForTabIndex(index) })}
                    >
                        {tab.label}
                    </TabButton>
                ))}
            </TabStrip>

            <UserContext.Provider value={shipperContext}>
                <ActiveTab />
            </UserContext.Provider>
        </PageSurface>
    );
};

export default CorporateShipper;
